/**
 * Hero Slideshow controller.
 * Self-contained, section-agnostic — every instance on the page manages
 * its own state via data-section-id, so multiple slideshows work independently.
 */
class HeroSlideshow {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-hs-track]');
    this.slides = Array.from(root.querySelectorAll('[data-hs-slide]'));
    this.dots = Array.from(root.querySelectorAll('[data-hs-nav-dots] .hero-slideshow__dot'));
    this.numbers = Array.from(root.querySelectorAll('[data-hs-nav-numbers] .hero-slideshow__number'));
    this.prevBtn = root.querySelector('[data-hs-prev]');
    this.nextBtn = root.querySelector('[data-hs-next]');

    this.index = 0;
    this.slideCount = this.slides.length;
    this.autoplay = root.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(root.dataset.autoplaySpeed, 10) || 5000;
    this.autoplayTimer = null;

    this.isDragging = false;
    this.dragStartX = 0;
    this.dragDeltaX = 0;
    this.trackWidth = 0;

    if (this.slideCount <= 1) return;

    this.bindEvents();
    this.goTo(0, false);
    this.startAutoplay();
  }

  bindEvents() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.hsGoto, 10)));
    });
    this.numbers.forEach((num) => {
      num.addEventListener('click', () => this.goTo(parseInt(num.dataset.hsGoto, 10)));
    });

    // Pointer-based swipe: works for mouse drag on desktop and touch on mobile.
    this.track.addEventListener('pointerdown', this.onPointerDown.bind(this));
    this.track.addEventListener('pointermove', this.onPointerMove.bind(this));
    this.track.addEventListener('pointerup', this.onPointerUp.bind(this));
    this.track.addEventListener('pointercancel', this.onPointerUp.bind(this));
    this.track.addEventListener('pointerleave', this.onPointerLeave.bind(this));

    this.root.addEventListener('mouseenter', () => this.stopAutoplay());
    this.root.addEventListener('mouseleave', () => this.startAutoplay());

    window.addEventListener('resize', () => this.goTo(this.index, false));
  }

  onPointerDown(e) {
    this.isDragging = true;
    this.dragStartX = e.clientX;
    this.dragDeltaX = 0;
    this.trackWidth = this.track.getBoundingClientRect().width;
    this.track.style.transition = 'none';
    this.stopAutoplay();
    try { this.track.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
  }

  onPointerMove(e) {
    if (!this.isDragging) return;
    this.dragDeltaX = e.clientX - this.dragStartX;
    const base = -this.index * this.trackWidth;
    this.track.style.transform = `translateX(${base + this.dragDeltaX}px)`;
  }

  onPointerUp() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.track.style.transition = '';

    const threshold = this.trackWidth * 0.15;
    if (this.dragDeltaX > threshold) {
      this.prev();
    } else if (this.dragDeltaX < -threshold) {
      this.next();
    } else {
      this.goTo(this.index, true);
    }
    this.startAutoplay();
  }

  onPointerLeave() {
    if (this.isDragging) this.onPointerUp();
  }

  goTo(index, animate = true) {
    if (index < 0) index = this.slideCount - 1;
    if (index >= this.slideCount) index = 0;
    this.index = index;

    this.track.style.transition = animate ? 'transform 0.45s ease' : 'none';
    this.track.style.transform = `translateX(-${index * 100}%)`;

    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    this.numbers.forEach((num, i) => num.classList.toggle('is-active', i === index));
  }

  next() { this.goTo(this.index + 1); }
  prev() { this.goTo(this.index - 1); }

  startAutoplay() {
    if (!this.autoplay || this.slideCount <= 1) return;
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => this.next(), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
}

function initHeroSlideshows(scope = document) {
  scope.querySelectorAll('[data-hero-slideshow]').forEach((root) => {
    if (root.dataset.hsInitialized) return;
    root.dataset.hsInitialized = 'true';
    new HeroSlideshow(root);
  });
}

document.addEventListener('DOMContentLoaded', () => initHeroSlideshows());

// Re-init when a slideshow section is added/edited in the Theme Editor.
document.addEventListener('shopify:section:load', (e) => initHeroSlideshows(e.target));
